import { useEffect, useMemo, useState, type ReactNode } from "react"
import { router } from "@inertiajs/react"
import { Lock, CircleDot, Archive as ArchiveIcon } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select } from "@/components/ui/select"
import { HelpHint } from "@/components/ui/help-hint"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import {
  EscalationReasonCatalogRow,
  CATALOG_GRID_COLS,
} from "@/components/domain/escalation-reason-catalog-row"
import { EscalationReasonEditPanel } from "@/components/domain/escalation-reason-edit-panel"
import { MergeReasonDialog } from "@/components/domain/merge-reason-dialog"
import { cn } from "@/lib/cn"
import type {
  EscalationReason,
  EscalationReasonsIndexProps,
  UpdateReasonBody,
} from "@/types/agent_escalation_reason"

// Single-page escalation reason workspace at /escalation_reasons. Layout:
//   PageHeader → Agent picker → status pills → pending callout →
//   two-column [Reason catalog | Edit reason]
// Filtering is client-side; mutations are Inertia router calls (CSRF +
// flash + page-state refresh handled by the controller's redirect-back).

const BASE = "/escalation_reasons"

type FilterKey = "all" | "pending" | "live" | "archived"
type SortKey   = "timesUsedDesc" | "timesUsedAsc" | "lastUsed" | "nameAsc"

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "timesUsedDesc", label: "Most used" },
  { value: "timesUsedAsc",  label: "Least used" },
  { value: "lastUsed",      label: "Recently classified" },
  { value: "nameAsc",       label: "Name A→Z" },
]

export default function AgentEscalationReasonsIndex({
  agent,
  agents,
  pending,
  live,
  archived,
}: EscalationReasonsIndexProps) {
  const [filter, setFilter] = useState<FilterKey>(pending.length > 0 ? "pending" : "live")
  const [search, setSearch] = useState("")
  const [sort, setSort] = useState<SortKey>("timesUsedDesc")
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showArchivedSection, setShowArchivedSection] = useState(false)
  const [mergeSource, setMergeSource] = useState<EscalationReason | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({ name: "", key: "", description: "" })
  const [submitting, setSubmitting] = useState(false)

  const allReasons = useMemo(
    () => [...pending, ...live, ...archived],
    [pending, live, archived],
  )

  const filteredReasons = useMemo(() => {
    const base =
      filter === "pending"  ? pending  :
      filter === "live"     ? live     :
      filter === "archived" ? archived :
                              [...pending, ...live, ...archived]
    const q = search.trim().toLowerCase()
    const matched = q
      ? base.filter((r) =>
          r.name.toLowerCase().includes(q) ||
          r.key.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q),
        )
      : base
    const rows = [...matched]
    rows.sort((a, b) => {
      if (sort === "nameAsc")      return a.name.localeCompare(b.name)
      if (sort === "timesUsedAsc") return a.timesUsed - b.timesUsed
      if (sort === "lastUsed") {
        const av = a.lastUsedAt ? Date.parse(a.lastUsedAt) : 0
        const bv = b.lastUsedAt ? Date.parse(b.lastUsedAt) : 0
        return bv - av
      }
      // timesUsedDesc (default)
      return b.timesUsed - a.timesUsed
    })
    return rows
  }, [filter, search, sort, pending, live, archived])

  useEffect(() => {
    if (selectedId !== null && allReasons.some((r) => r.id === selectedId)) return
    setSelectedId(filteredReasons[0]?.id ?? null)
  }, [allReasons, filteredReasons, selectedId])

  const selected = useMemo(
    () => allReasons.find((r) => r.id === selectedId) ?? null,
    [allReasons, selectedId],
  )

  const handleAgentChange = (agentId: number) => {
    router.get(BASE, { agent_id: agentId }, { preserveScroll: true, preserveState: false })
  }

  const patchReason = (id: number, body: UpdateReasonBody) => {
    router.patch(`${BASE}/${id}`, { agent_escalation_reason: body }, { preserveScroll: true })
  }

  const approve  = (r: EscalationReason) => patchReason(r.id, { status: "live" })
  const archive  = (r: EscalationReason) => patchReason(r.id, { status: "archived" })
  const restore  = (r: EscalationReason) => patchReason(r.id, { status: "live" })
  const rename   = (r: EscalationReason, name: string) => patchReason(r.id, { name })
  const editDesc = (r: EscalationReason, description: string) => patchReason(r.id, { description })

  const confirmMerge = (targetId: number) => {
    if (!mergeSource) return
    router.post(`${BASE}/${mergeSource.id}/merge_into`, { target_id: targetId }, {
      preserveScroll: true,
      onFinish: () => setMergeSource(null),
    })
  }

  const submitCreate = () => {
    const name = draft.name.trim()
    const key  = draft.key.trim()
    const description = draft.description.trim()
    if (!name || !key || !description) return
    setSubmitting(true)
    router.post(BASE, {
      agent_id: agent.id,
      agent_escalation_reason: { name, key, description, status: "live" },
    }, {
      preserveScroll: true,
      onFinish: () => {
        setSubmitting(false)
        setCreating(false)
        setDraft({ name: "", key: "", description: "" })
      },
    })
  }

  const mergeCandidates = useMemo(() => {
    if (!mergeSource) return []
    return allReasons.filter((r) => r.id !== mergeSource.id)
  }, [allReasons, mergeSource])

  const counts = {
    all:      pending.length + live.length + archived.length,
    pending:  pending.length,
    live:     live.length,
    archived: archived.length,
  }

  return (
    <AppShell>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <PageHeader
            eyebrow="Agents"
            title="Escalation Reasons"
            subtitle="Why conversations needed a human."
            actions={<Button onClick={() => setCreating(true)} className="w-full sm:w-auto">+ New reason</Button>}
          />
        </div>
        <div className="hidden pt-9 md:block">
          <HelpHint
            heading="What are escalation reasons?"
            body={
              <>
                <p>
                  Categorical buckets answering <em>why</em> a convo needed a human
                  — distinct from topics (which answer <em>what</em> the convo is about).
                </p>
                <p className="mt-2">
                  Haiku proposes new reasons; you approve, rename, merge, or archive.
                  Live reasons feed analytics and the classifier prompt.
                </p>
              </>
            }
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="text-xs uppercase tracking-wide text-app-muted">Agent</label>
        <div className="w-full sm:min-w-[220px] sm:w-auto">
          <Select<number>
            value={{ value: agent.id, label: agent.name }}
            onChange={(o) => o && handleAgentChange(o.value)}
            options={agents.map((a) => ({ value: a.id, label: a.name }))}
            isSearchable={agents.length > 6}
          />
        </div>
      </div>

      {/* Status tabs — visual lifecycle summary that also drives the catalog filter. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatusTab
          active={filter === "pending"}
          onClick={() => setFilter("pending")}
          icon={<Lock size={14} />}
          tone="red"
          label="Pending review"
          count={counts.pending}
        />
        <StatusTab
          active={filter === "live"}
          onClick={() => setFilter("live")}
          icon={<CircleDot size={14} />}
          tone="green"
          label="Live"
          count={counts.live}
        />
        <StatusTab
          active={filter === "archived"}
          onClick={() => setFilter("archived")}
          icon={<ArchiveIcon size={14} />}
          tone="muted"
          label="Archived"
          count={counts.archived}
        />
      </div>

      {/* Pending review callout — always visible. Copy + tone shift based on count. */}
      <Card className={cn(
        pending.length > 0
          ? "border-brand-red/30 ring-1 ring-brand-red/10"
          : "border-border",
      )}>
        <div className={cn(
          "flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center",
          pending.length > 0 && "bg-brand-red/5",
        )}>
          <div className="flex items-start gap-3 sm:flex-1">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-brand-red/30 bg-brand-redSoft text-brand-red">
              <Lock size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-app-text">Pending review</div>
              <div className="text-xs text-app-muted">
                {pending.length === 0
                  ? "Nothing to review."
                  : `${pending.length} proposed by the classifier — approve, rename, merge, or archive.`}
              </div>
            </div>
          </div>
          {pending.length > 0 && (
            <Button
              variant="red"
              size="sm"
              onClick={() => { setFilter("pending"); setSelectedId(pending[0]?.id ?? null) }}
              className="w-full sm:w-auto"
            >
              Review pending
            </Button>
          )}
        </div>
      </Card>

      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        {/* Catalog (left, 2/3) */}
        <div className="lg:col-span-2 min-w-0">
          <Card className="overflow-hidden p-0">
            <header className="space-y-3 border-b border-border p-4">
              <div>
                <h2 className="text-base font-semibold text-app-text">Reason catalog</h2>
                <p className="mt-0.5 text-sm text-app-muted">
                  Browse, search, and manage escalation reasons.
                </p>
              </div>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search reasons…"
                className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text placeholder:text-app-muted focus:border-brand-red focus:outline-none"
              />
            </header>

            <div className="flex items-center justify-between border-b border-border bg-app-panel2/30 px-4 py-2 text-[11px] text-app-muted">
              <div>{filteredReasons.length} reason{filteredReasons.length === 1 ? "" : "s"}</div>
              <div className="min-w-[200px]">
                <Select<SortKey>
                  value={SORT_OPTIONS.find((o) => o.value === sort) ?? SORT_OPTIONS[0]}
                  options={SORT_OPTIONS}
                  onChange={(o) => o && setSort(o.value)}
                  isSearchable={false}
                />
              </div>
            </div>

            {filteredReasons.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-app-muted">
                {search.trim()
                  ? "No reasons match your search."
                  : filter === "pending"
                    ? "Nothing to review."
                    : filter === "archived"
                      ? "Nothing archived yet."
                      : "No reasons yet — create one or wait for the classifier to propose."}
              </div>
            ) : (
              <>
                <div
                  className={cn(
                    CATALOG_GRID_COLS,
                    "hidden border-b border-border bg-app-panel2/20 px-4 py-2 text-[11px] uppercase tracking-wide text-app-muted md:grid",
                  )}
                >
                  <div />
                  <div>Name</div>
                  <div>Used</div>
                  <div>Last used</div>
                  <div>Provenance</div>
                </div>
                <div>
                  {filteredReasons.map((r) => (
                    <EscalationReasonCatalogRow
                      key={r.id}
                      reason={r}
                      selected={r.id === selectedId}
                      onClick={() => setSelectedId(r.id)}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Archived collapsible — only when not already shown via the filter. */}
            {filter !== "archived" && filter !== "all" && (
              <div className="border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowArchivedSection((v) => !v)}
                  className="flex w-full items-center justify-between px-5 py-3 text-left transition hover:bg-white/5"
                >
                  <div>
                    <div className="text-sm font-medium text-app-text">Archived reasons</div>
                    <div className="text-xs text-app-muted">
                      {archived.length === 0
                        ? "No archived reasons."
                        : `${archived.length} hidden from the classifier.`}
                    </div>
                  </div>
                  <span className={cn("text-app-muted transition", showArchivedSection && "rotate-90")}>›</span>
                </button>
                {showArchivedSection && archived.length > 0 && (
                  <div className="border-t border-border">
                    {archived.map((r) => (
                      <EscalationReasonCatalogRow
                        key={r.id}
                        reason={r}
                        selected={r.id === selectedId}
                        onClick={() => setSelectedId(r.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Edit panel (right, 1/3) */}
        <div className="lg:col-span-1 min-w-0">
          <Card className="lg:sticky lg:top-2 lg:self-start lg:max-h-[calc(100vh-2rem)] overflow-y-auto p-0">
            <EscalationReasonEditPanel
              reason={selected}
              onApprove={approve}
              onArchive={archive}
              onRestore={restore}
              onMerge={(r) => setMergeSource(r)}
              onRename={rename}
              onEditDescription={editDesc}
            />
          </Card>
        </div>
      </div>

      <MergeReasonDialog
        open={mergeSource !== null}
        source={mergeSource}
        candidates={mergeCandidates}
        onClose={() => setMergeSource(null)}
        onConfirm={confirmMerge}
      />

      <Dialog open={creating} onClose={() => !submitting && setCreating(false)} size="md">
        <DialogHeader title="New escalation reason" subtitle="Created live — skips the review queue." />
        <DialogBody>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-app-muted">Name</label>
            <Input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="Bug in cashout flow"
              className="w-full"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-app-muted">Key</label>
            <Input
              type="text"
              value={draft.key}
              onChange={(e) => setDraft((d) => ({ ...d, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))}
              placeholder="bug_in_cashout_flow"
              className="w-full font-mono"
            />
            <p className="text-[11px] text-app-muted">Lowercase letters, digits, underscores. Locked after save.</p>
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-app-muted">Description</label>
            <Textarea
              rows={4}
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder="1-3 sentences. Used by the classifier prompt to decide if this reason fits."
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setCreating(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submitCreate}
            disabled={submitting || !draft.name.trim() || !draft.key.trim() || !draft.description.trim()}
          >
            {submitting ? "Creating…" : "Create reason"}
          </Button>
        </DialogFooter>
      </Dialog>
    </AppShell>
  )
}

function StatusTab({
  active,
  onClick,
  icon,
  tone,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  icon: ReactNode
  tone: "red" | "green" | "muted"
  label: string
  count: number
}) {
  const iconWrap =
    tone === "red"   ? "border-brand-red/30 bg-brand-redSoft text-brand-red" :
    tone === "green" ? "border-status-success/30 bg-status-success/10 text-status-success" :
                       "border-border bg-white/5 text-app-muted"
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 rounded-control border bg-app-panel2/60 px-4 py-3 text-left transition",
        active
          ? "border-brand-red/50 ring-1 ring-brand-red/20"
          : "border-border hover:bg-white/5",
      )}
    >
      <span className={cn("flex h-7 w-7 items-center justify-center rounded-full border", iconWrap)}>
        {icon}
      </span>
      <span className="flex-1 text-sm font-medium text-app-text">{label}</span>
      <span className="inline-flex min-w-[26px] items-center justify-center rounded-chip bg-white/5 px-2 py-0.5 text-xs font-semibold tabular-nums text-app-text">
        {count}
      </span>
    </button>
  )
}

